import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import aiReportsService, { AIReportAttachment, AIReportDetail } from '../../services/ai-reports.service';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { sanitizeHtml } from '../../lib/sanitizeHtml';
import RiskMatrixVisualization from '../../components/ai-reports/RiskMatrixVisualization';
import { LedgerSkeleton } from '../../components/shell/LedgerSkeleton';
import { reportStatus, useDeleteReport, useReportDetail, useReportStatus } from '../../hooks/useReportQueries';
import { formatDateTime, formatDateRange } from '../../utils/dateTime';
import { useTimeFormat } from '../../hooks/useTimeFormat';

const AIReportViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const use24Hour = useTimeFormat();

  const { data: report, isPending, isError, error } = useReportDetail(id ?? null);
  const status = report ? reportStatus(report) : null;

  // Safety net: generation is synchronous server-side, so a GENERATING row is
  // only observable in rare cross-tab timing. While it is, poll — the hook
  // invalidates the detail query internally on the terminal transition, and
  // the refetch flips this view. No invalidation wiring belongs on the page.
  useReportStatus(id ?? null, status === 'GENERATING');

  const deleteReport = useDeleteReport();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAttachments, setDownloadingAttachments] = useState<Set<string>>(new Set());

  const handleDownload = async (format: 'html' | 'pdf' | 'docx') => {
    if (!report) return;

    try {
      setDownloading(true);
      await aiReportsService.downloadReport(report.id, format);
    } catch (err) {
      console.error('Download failed:', err);
      setActionError('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const confirmDelete = () => {
    if (!report || deleteReport.isPending) return;
    setConfirmingDelete(false);
    deleteReport.mutate(report.id, {
      // Navigate back to the reports list
      onSuccess: () => navigate('/ai-reports'),
      onError: (err) => setActionError(err.message || 'Failed to delete report'),
    });
  };

  const handleDownloadAttachment = async (attachment: AIReportAttachment) => {
    try {
      // Add to downloading set
      setDownloadingAttachments(prev => new Set(prev).add(attachment.id));

      await aiReportsService.downloadAttachment(report!.id, attachment.id, attachment.file_name);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      setActionError('Failed to download attachment');
    } finally {
      // Remove from downloading set
      setDownloadingAttachments(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  const getOrganizationName = () => {
    if (report?.company) return report.company.name;
    if (report?.team) return report.team.name;
    if (report?.department) return report.department.name;
    return 'Unknown Organization';
  };

  const backButton = (
    <button
      type="button"
      onClick={() => navigate('/ai-reports')}
      className="cap-label cursor-pointer hover:!text-ink"
    >
      ← Back to reports
    </button>
  );

  if (isPending) {
    return (
      <div className="px-6 py-8" data-print-hide>
        <div className="ledger-doc mx-auto">
          <LedgerSkeleton lines={8} />
        </div>
      </div>
    );
  }

  if (isError || !report) {
    return (
      <div className="px-6 py-8">
        <div className="ledger-doc mx-auto">
          <p role="alert" className="border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {(error instanceof Error && error.message) || 'Report not found'}
          </p>
          <div className="mt-3">{backButton}</div>
        </div>
      </div>
    );
  }

  const snapshotScores = (risk: AIReportDetail['included_risks'][number]) => ({
    inherent:
      risk.risk_data_snapshot.inherent_score ||
      risk.risk_data_snapshot.inherent_likelihood * risk.risk_data_snapshot.inherent_impact,
    residual:
      risk.risk_data_snapshot.residual_score ||
      risk.risk_data_snapshot.residual_likelihood * risk.risk_data_snapshot.residual_impact,
  });

  return (
    <div className="px-6 py-8">
      <article className="ledger-doc mx-auto">
        <header>
          <div data-print-hide className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
            {backButton}
            <span className="flex items-baseline gap-3">
              {status === 'COMPLETED' &&
                report.export_formats.map((format) => (
                  <button
                    key={format.format}
                    type="button"
                    onClick={() => handleDownload(format.format as 'html' | 'pdf' | 'docx')}
                    disabled={downloading}
                    className="cap-label cursor-pointer hover:!text-accent disabled:cursor-default disabled:opacity-40"
                  >
                    {format.name}
                  </button>
                ))}
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                disabled={deleteReport.isPending}
                className="cap-label cursor-pointer hover:!text-sev-critical disabled:cursor-default disabled:opacity-40"
              >
                Delete
              </button>
            </span>
          </div>

          <h1>{formatDateRange(report.date_from, report.date_to)}</h1>
          <p className="cap-label !my-2">
            {getOrganizationName()} · {formatDateRange(report.date_from, report.date_to)} · generated{' '}
            {formatDateTime(report.created_at, use24Hour)}
          </p>
          <p className="!mt-0 font-serif text-scale-1 italic text-ink-muted">
            By {report.created_by.full_name || report.created_by.email}
          </p>
        </header>

        {actionError && (
          <p
            data-print-hide
            role="alert"
            className="border border-sev-critical/40 px-3 py-2 !text-scale-1 text-sev-critical"
          >
            {actionError}
          </p>
        )}

        {status === 'GENERATING' && (
          <section aria-label="Generation status" className="mt-6" data-print-hide>
            <p role="status" className="font-serif text-scale-1 italic text-ink-muted">
              Generating this report — the page refreshes on its own once it is ready.
            </p>
            <LedgerSkeleton lines={8} className="mt-4" />
          </section>
        )}

        {status === 'FAILED' && (
          <section aria-label="Generation error" className="mt-6">
            <p role="alert" className="border border-sev-critical/40 px-3 py-2 !text-scale-1 text-sev-critical">
              Report generation failed: {report.error_message}
            </p>
            <p className="text-scale-1 text-ink-muted">
              This entry holds no document. Delete it and generate the report again.
            </p>
          </section>
        )}

        {status === 'COMPLETED' && (
          <>
            <section aria-label="Executive summary">
              <h2>Executive summary</h2>
              <p className="whitespace-pre-wrap">{report.ai_overview}</p>
            </section>

            <section aria-label="Risk statistics">
              <h2>Risk statistics</h2>
              <table>
                <thead>
                  <tr>
                    <th>Total risks</th>
                    <th>High inherent</th>
                    <th>High residual</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{report.risk_count}</td>
                    <td>{report.high_inherent_count}</td>
                    <td>{report.high_residual_count}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section aria-label="Risk matrices">
              <h2>Risk matrices</h2>
              <h3>Inherent risk matrix</h3>
              <RiskMatrixVisualization data={report.inherent_matrix_data.data} type="inherent" />
              <h3>Residual risk matrix</h3>
              <RiskMatrixVisualization data={report.residual_matrix_data.data} type="residual" />
            </section>

            {report.notes && (
              <section aria-label="Notes">
                <h2>Notes</h2>
                {/* Self-generated rich text from this product's own editor — same
                    rendering posture as the page this replaces. */}
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(report.notes) }} />
              </section>
            )}

            {report.included_risks.length > 0 && (
              <section aria-label="Included risks">
                <h2>Included risks</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Risk</th>
                      <th className="text-right">Inherent</th>
                      <th className="text-right">Residual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.included_risks.map((risk) => {
                      const scores = snapshotScores(risk);
                      return (
                        <tr key={risk.id} data-testid="included-risk-row">
                          <td>{risk.risk_reference}</td>
                          <td className="font-sans text-scale-1">{risk.risk_title}</td>
                          <td className="text-right">{scores.inherent}</td>
                          <td className="text-right">{scores.residual}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}

        {report.attachments && report.attachments.length > 0 && (
          <section aria-label="Attachments" data-print-hide>
            <h2>Attachments</h2>
            <table>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Size</th>
                  <th>Uploaded by</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {report.attachments.map((attachment) => (
                  <tr key={attachment.id}>
                    <td>{attachment.file_name}</td>
                    <td>{(attachment.file_size / 1024 / 1024).toFixed(2)} MB</td>
                    <td>{attachment.uploaded_by_name}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        onClick={() => handleDownloadAttachment(attachment)}
                        disabled={downloadingAttachments.has(attachment.id)}
                        className="cap-label cursor-pointer hover:!text-accent disabled:cursor-default disabled:opacity-40"
                      >
                        {downloadingAttachments.has(attachment.id) ? 'Downloading…' : 'Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </article>

      <ConfirmDialog
        open={confirmingDelete}
        title="Delete report?"
        message="This action cannot be undone."
        confirmText="Delete"
        severity="error"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
};

export default AIReportViewPage;
